import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MicroserviceBox, ModalsProvider } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';
import { useReadLocalStorage } from '@speeki/storage-manager';

import { HotspotSevice } from '@services/HotspotService/HotspotService';
import { getPath } from '@services/RouteService/paths';

import { useReactiveVar } from '@apollo/client';
import {
    authVar,
    servicesStatistics,
    subsidiarySiteUuidFromUrlVar,
    tenantModulesVar,
} from '@boot/reactiveVariables';

import {
    MicroservicesTilesInnerWrapper,
    MicroservicesTilesWrapper,
    SubsidiaryWarningWrapper,
    SwitchToEntity,
    SwitchToEntityText,
    TileButton,
    TilesButtons,
    TilesTitle,
} from './MicroservicesTiles.styled';

const EXPANDED_TILE_OFFSET = 80;

interface MicroservicesTilesProps {
    modules: {
        cases: boolean;
        disclosures: boolean;
        surveys: boolean;
    };
}

export const MicroservicesTiles = ({ modules }: MicroservicesTilesProps) => {
    const navigate = useNavigate();

    const ref = useRef<HTMLDivElement>(null);
    const [sectionHeight, setSectionHeight] = useState<number | undefined>(
        undefined,
    );

    const hideDashboardHint = useReadLocalStorage('hideDashboardHint');

    const { isGuest, isLoggedIn } = useReactiveVar(authVar);
    const isSubsidiaryEnabled =
        useReactiveVar(tenantModulesVar).subsidiarySites;
    const subsidiarySiteUuidFromUrl = useReactiveVar(
        subsidiarySiteUuidFromUrlVar,
    );
    const isSubsidiaryChosen = isSubsidiaryEnabled && subsidiarySiteUuidFromUrl;
    const {
        disclosuresCount,
        ongoingCases,
        respondedDisclosures,
        surveysCount,
    } = useReactiveVar(servicesStatistics);

    const { t } = useTranslation();

    const renderSubtext = useMemo(() => {
        if (!isLoggedIn)
            return {
                disclosures: t('DISCLOSURES_ACTION_SUBTITLE'),
                messaging: t('MESSAGING_ACTION_SUBTITLE_NOT_LOGGED_IN'),
                surveys: t('SURVEYS_ACTION_SUBTITLE'),
            };

        if (isGuest)
            return {
                disclosures: `${disclosuresCount} ${t('FORMS_AVAILABLE')}`,
                messaging: '',
                surveys: `${surveysCount} ${t('SURVEYS_WAITING')}`,
            };

        return {
            disclosures: `${respondedDisclosures} ${t('FORMS_SUBMITTED')}`,
            messaging: `${ongoingCases} ${t('ONGOING')}`,
            surveys: `${surveysCount} ${t('SURVEYS_WAITING')}`,
        };
    }, [disclosuresCount, isGuest, isLoggedIn, ongoingCases, surveysCount]);

    const handleSetSectionHeight = () =>
        ref.current &&
        setSectionHeight(ref.current.offsetHeight + EXPANDED_TILE_OFFSET);

    useEffect(() => {
        window.addEventListener('resize', handleSetSectionHeight);
        handleSetSectionHeight();

        return () =>
            window.removeEventListener('resize', handleSetSectionHeight);
    }, []);

    return (
        <MicroservicesTilesWrapper sectionHeight={sectionHeight}>
            <MicroservicesTilesInnerWrapper ref={ref}>
                <TilesTitle>
                    {t(
                        isLoggedIn
                            ? 'WHAT_CAN_WE_DO_FOR_YOU'
                            : 'HOW_CAN_WE_HELP_YOU_TODAY',
                    )}
                </TilesTitle>
                {modules.cases && (
                    <MicroserviceBox
                        hotspotProps={{
                            isVisible: !hideDashboardHint,
                            onClear: () => {
                                HotspotSevice.setStorageHotspot(
                                    'hideDashboardHint',
                                    'true',
                                );
                            },
                        }}
                        iconProps={{
                            icon: 'SvgMessaging',
                            size: 32,
                        }}
                        isStatic
                        subtext={renderSubtext.messaging}
                        testId="vSOxOgH"
                        title={t('MESSAGING_ACTION_TITLE')}
                    >
                        <TilesButtons>
                            {isGuest ? (
                                <TileButton
                                    onClick={() =>
                                        navigate(
                                            getPath({ path: '/incidents' }),
                                        )
                                    }
                                    size="small"
                                    testId="button_check-case-status"
                                    variant="secondary"
                                >
                                    {t('CHECK_CASE_STATUS')}
                                </TileButton>
                            ) : (
                                <TileButton
                                    onClick={() =>
                                        isLoggedIn
                                            ? navigate(
                                                  getPath({
                                                      path: '/incidents/reports',
                                                  }),
                                              )
                                            : navigate(
                                                  getPath({
                                                      path: '/auth/see-my-incidents',
                                                  }),
                                              )
                                    }
                                    size="small"
                                    testId="button_see-my-cases"
                                    variant="secondary"
                                >
                                    {t('SEE_MY_CASES')}
                                </TileButton>
                            )}
                            <TileButton
                                onClick={() =>
                                    isLoggedIn
                                        ? navigate(
                                              getPath({
                                                  path: '/new-conversation',
                                              }),
                                          )
                                        : navigate(
                                              getPath({
                                                  path: '/auth/start-new-report',
                                              }),
                                          )
                                }
                                size="small"
                                testId="button_start-new-report"
                                variant="primary"
                            >
                                {t('START_NEW_REPORT')}
                            </TileButton>
                        </TilesButtons>
                    </MicroserviceBox>
                )}
                {modules.disclosures &&
                    !(!isLoggedIn && isSubsidiaryChosen) && (
                        <MicroserviceBox
                            iconProps={{
                                icon: 'SvgDAndD',
                                size: 32,
                            }}
                            isStatic
                            subtext={
                                !isSubsidiaryChosen
                                    ? renderSubtext.disclosures
                                    : undefined
                            }
                            testId="WdrWqCn"
                            title={t('DISCLOSURES_ACTION_TITLE')}
                        >
                            {!isSubsidiaryChosen ? (
                                <TilesButtons>
                                    <TileButton
                                        onClick={() =>
                                            isLoggedIn
                                                ? navigate(
                                                      getPath({
                                                          path: '/disclosure/list',
                                                      }),
                                                  )
                                                : navigate(
                                                      getPath({
                                                          path: '/auth/declare-or-disclose-information',
                                                      }),
                                                  )
                                        }
                                        size="small"
                                        testId="button_see-my-forms"
                                        variant="secondary"
                                    >
                                        {t('SEE_MY_FORMS')}
                                    </TileButton>
                                    <TileButton
                                        onClick={() =>
                                            isLoggedIn
                                                ? navigate(
                                                      getPath({
                                                          path: '/disclosure/list',
                                                      }),
                                                  )
                                                : navigate(
                                                      getPath({
                                                          path: '/auth/declare-or-disclose-information',
                                                      }),
                                                  )
                                        }
                                        size="small"
                                        testId="button_submit-new-form"
                                        variant="primary"
                                    >
                                        {t('SUBMIT_NEW_FORM')}
                                    </TileButton>
                                </TilesButtons>
                            ) : (
                                <SubsidiaryWarningWrapper>
                                    <IconManager icon={'SvgInfo'} size={16} />
                                    <SwitchToEntityText>
                                        {t('DISCLOSURE_MICROSERVICE_BOX_TEXT')}{' '}
                                        <SwitchToEntity
                                            onClick={() =>
                                                ModalsProvider.open(
                                                    'EntityChangeModal',
                                                )
                                            }
                                        >
                                            {t('SWITCH').toLowerCase()}
                                        </SwitchToEntity>{' '}
                                        {t('TO_HEADQUARTERS')}
                                    </SwitchToEntityText>
                                </SubsidiaryWarningWrapper>
                            )}
                        </MicroserviceBox>
                    )}
                {modules.surveys && !(!isLoggedIn && isSubsidiaryChosen) && (
                    <MicroserviceBox
                        iconProps={{
                            icon: 'SvgSurvey',
                            size: 32,
                        }}
                        isStatic
                        subtext={
                            !isSubsidiaryChosen
                                ? renderSubtext.surveys
                                : undefined
                        }
                        testId="kTU3cqZ"
                        title={t('PARTICIPATE_IN_SURVEY')}
                    >
                        {!isSubsidiaryChosen ? (
                            <TileButton
                                onClick={() =>
                                    isLoggedIn
                                        ? navigate(
                                              getPath({ path: '/survey/list' }),
                                          )
                                        : navigate(
                                              getPath({
                                                  path: '/auth/see-active-surveys',
                                              }),
                                          )
                                }
                                size="small"
                                testId="button_see-active-surveys"
                                variant="primary"
                            >
                                {t('SEE_ACTIVE_SURVEYS')}
                            </TileButton>
                        ) : (
                            <SubsidiaryWarningWrapper>
                                <IconManager icon={'SvgInfo'} size={16} />
                                <SwitchToEntityText>
                                    {t('SURVEY_MICROSERVICE_BOX_TEXT')}{' '}
                                    <SwitchToEntity
                                        onClick={() =>
                                            ModalsProvider.open(
                                                'EntityChangeModal',
                                            )
                                        }
                                    >
                                        {t('SWITCH').toLowerCase()}
                                    </SwitchToEntity>{' '}
                                    {t('TO_HEADQUARTERS')}
                                </SwitchToEntityText>
                            </SubsidiaryWarningWrapper>
                        )}
                    </MicroserviceBox>
                )}
            </MicroservicesTilesInnerWrapper>
        </MicroservicesTilesWrapper>
    );
};
